/**
 * PromptCategoryBadge — small badge keyed off `knowledge__prompt.category`
 * for prompt cards. Push #22 rule 3: prompts don't have vendor logos (no
 * vendor), so the category fills the visual-uniqueness slot that vendor logos
 * fill for connectors + models.
 *
 * Rendered with Foundation's Badge (soft, pill, sm). A category is metadata,
 * not a status, so it doesn't borrow Foundation's meaning-carrying tones
 * (success, warning, error, info): the eight known categories render in the
 * accent tone and anything else in neutral. The old per-category colour
 * palette was dropped on purpose (PLAT-323). Foundation's Badge is uppercase
 * by design.
 *
 * Used on prompt cards in:
 *   - /admin/prompts list
 *   - /prompts list
 *   - PS workshop PromptsWidget assigned rows
 *   - Catalog "Prompts" tab
 *   - Chat rail "Insert prompt" chips (compact mode)
 *
 * Stays OFF the page-header lightning tile — the generic teal squiggle
 * (ObjectIcon kind="prompts") owns that surface per Push #12.
 */

import { Badge } from '@olytic/foundation';
import type { ReactNode } from 'react';

import { formatLabel } from '../lib/format.ts';

interface PromptCategoryBadgeProps {
  category: string | null | undefined;
  /** Kept for call-site compatibility; both sizes render Badge size="sm". */
  size?: 'xs' | 'sm';
}

interface CategoryMeta {
  label: string;
  known: boolean;
}

/**
 * Labels for the seeded categories. Anything else is not "known". A Map, not
 * an object literal: category is free text, and an object lookup would match
 * inherited keys such as "__proto__" or "constructor".
 */
const LABELS: ReadonlyMap<string, string> = new Map([
  ['sales', 'Sales'],
  ['productivity', 'Productivity'],
  ['data', 'Data'],
  ['support', 'Support'],
  ['marketing', 'Marketing'],
  ['engineering', 'Engineering'],
  ['finance', 'Finance'],
  ['ops', 'Ops'],
]);

function categoryMeta(raw: string | null | undefined): CategoryMeta {
  if (raw === null || raw === undefined || raw === '') return { label: 'Other', known: false };
  const label = LABELS.get(raw.trim().toLowerCase());
  return label === undefined ? { label: formatLabel(raw), known: false } : { label, known: true };
}

export function PromptCategoryBadge({ category }: PromptCategoryBadgeProps): ReactNode {
  const meta = categoryMeta(category);
  return (
    <Badge tone={meta.known ? 'accent' : 'neutral'} variant="soft" shape="pill" size="sm">
      {meta.label}
    </Badge>
  );
}
